import { Box, Typography } from "@mui/material";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import type { Theme } from "@mui/material/styles";
import { RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT } from "./ruleGraphControlDimensions/RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT";

const zoomButtonSx = (disabled: boolean) => (theme: Theme) => ({
  alignItems: "center",
  backgroundColor: "transparent",
  border: 0,
  borderRadius: "6px",
  cursor: disabled ? "not-allowed" : "pointer",
  display: "flex",
  height: 22,
  justifyContent: "center",
  padding: 0,
  width: 22,
  "&:hover:not(:disabled)": {
    backgroundColor: theme.igBackgroundColors.interactive.neutral.default,
  },
  "&:active:not(:disabled)": {
    backgroundColor: theme.igPrimitives.gray[400],
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.text.interactive.neutral.default}`,
    outlineOffset: 1,
  },
});

export const RuleGraphZoomControl = ({
  zoomPercentage,
  onZoomIn,
  onZoomOut,
  zoomInDisabled = false,
  zoomOutDisabled = false,
}: {
  zoomPercentage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
}) => {
  const [isZoomOutHovered, setIsZoomOutHovered] = useState(false);
  const [isZoomOutPressed, setIsZoomOutPressed] = useState(false);
  const showActiveZoomOutIcon =
    !zoomOutDisabled && (isZoomOutHovered || isZoomOutPressed);

  return (
    <Box
      component="div"
      sx={(theme) => ({
        alignItems: "center",
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        boxShadow: theme.igShadows.low,
        boxSizing: "border-box",
        display: "flex",
        gap: 0,
        height: RULE_GRAPH_VIEWPORT_CONTROL_HEIGHT,
        justifyContent: "center",
        px: 1,
        py: 1,
        width: 78,
      })}
    >
      <Box
        aria-label="Zoom out"
        component="button"
        disabled={zoomOutDisabled}
        onClick={onZoomOut}
        onMouseEnter={() => setIsZoomOutHovered(true)}
        onMouseLeave={() => setIsZoomOutHovered(false)}
        onPointerCancel={() => setIsZoomOutPressed(false)}
        onPointerDown={() => setIsZoomOutPressed(true)}
        onPointerLeave={() => setIsZoomOutPressed(false)}
        onPointerUp={() => setIsZoomOutPressed(false)}
        sx={zoomButtonSx(zoomOutDisabled)}
        type="button"
      >
        <Box
          aria-hidden="true"
          component={Minus}
          data-testid="rule-graph-zoom-minus-icon"
          size={13}
          strokeWidth={1.33}
          sx={(theme) => ({
            color: showActiveZoomOutIcon
              ? theme.palette.text.primary
              : theme.palette.text.tertiary,
          })}
        />
      </Box>
      <Typography sx={{ fontSize: 10, lineHeight: 1.5 }}>
        {zoomPercentage}%
      </Typography>
      <Box
        aria-label="Zoom in"
        component="button"
        disabled={zoomInDisabled}
        onClick={onZoomIn}
        sx={zoomButtonSx(zoomInDisabled)}
        type="button"
      >
        <Box
          aria-hidden="true"
          component={Plus}
          data-testid="rule-graph-zoom-plus-icon"
          size={13}
          strokeWidth={1.5}
          sx={(theme) => ({ color: theme.palette.text.tertiary })}
        />
      </Box>
    </Box>
  );
};
